import { storage, appwriteConfig } from '../config/appwrite';
import { ID } from 'appwrite';

export class StorageService {
  private static bucketId = appwriteConfig.storageBuckets.articleImages;

  /**
   * Lädt ein Bild in den Storage Bucket hoch
   */
  static async uploadImage(file: File): Promise<string> {
    try {
      // Generiere eine eindeutige Datei-ID
      const fileId = ID.unique();
      
      // Bestimme die Dateiendung
      // const fileExtension = file.name.split('.').pop() || 'jpg';
      
      // Lade die Datei hoch
      const uploadedFile = await storage.createFile(
        this.bucketId,
        fileId,
        file
      );
      
      // Erstelle eine authentifizierte URL für die hochgeladene Datei
      // Verwende getFileDownload für authentifizierte URLs
      const fileUrl = storage.getFileDownload(this.bucketId, uploadedFile.$id);
      
      return fileUrl.toString();
    } catch (error) {
      throw new Error('Fehler beim Hochladen des Bildes');
    }
  }

  /**
   * Löscht ein Bild aus dem Storage Bucket
   */
  static async deleteImage(fileId: string): Promise<void> {
    try {
      await storage.deleteFile(this.bucketId, fileId);
    } catch (error) {
      throw new Error('Fehler beim Löschen des Bildes');
    }
  }

  /**
   * Prüft, ob eine Datei ein gültiges Bild ist
   */
  static isValidImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 30 * 1024 * 1024; // 30MB
    
    return validTypes.includes(file.type) && file.size <= maxSize;
  }

  /**
   * Erstellt eine authentifizierte URL für ein Bild
   * Diese Methode kann verwendet werden, um bestehende Bild-URLs zu konvertieren
   */
  static getAuthenticatedImageUrl(fileId: string): string {
    try {
      // Prüfe, ob die URL bereits authentifiziert ist (enthält /download/)
      if (fileId.includes('/download/')) {
        return fileId;
      }
      
      // Extrahiere die File-ID aus der URL, falls eine vollständige URL übergeben wurde
      const extractedFileId = fileId.includes('/') ? fileId.split('/').pop() : fileId;
      
      if (!extractedFileId) {
        throw new Error('Ungültige File-ID');
      }
      
      // Erstelle eine authentifizierte URL
      const fileUrl = storage.getFileDownload(this.bucketId, extractedFileId);
      return fileUrl.toString();
    } catch (error) {
      // Fallback zur ursprünglichen URL
      return fileId;
    }
  }

  /**
   * Prüft, ob eine URL bereits authentifiziert ist
   */
  static isAuthenticatedUrl(url: string): boolean {
    return url.includes('/download/');
  }

  /**
   * Konvertiert eine getFileView URL zu einer getFileDownload URL
   */
  static convertToAuthenticatedUrl(viewUrl: string): string {
    try {
      // Extrahiere die File-ID aus der getFileView URL
      const urlParts = viewUrl.split('/');
      const fileId = urlParts[urlParts.length - 2]; // Die File-ID ist vor dem letzten Segment
      
      if (!fileId) {
        throw new Error('Konnte File-ID nicht aus URL extrahieren');
      }
      
      // Erstelle eine getFileDownload URL
      const downloadUrl = storage.getFileDownload(this.bucketId, fileId);
      return downloadUrl.toString();
    } catch (error) {
      return viewUrl; // Fallback zur ursprünglichen URL
    }
  }

  /**
   * Extrahiert eine File-ID aus einer Appwrite Storage URL
   */
  static extractFileIdFromStorageUrl(storageUrl: string): string | null {
    try {
      if (!storageUrl) return null;
      const match = storageUrl.match(/\/files\/([^/]+)\//);
      return match ? match[1] : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Konvertiert eine beliebige Storage-URL zu einer CORS-freundlichen View-URL
   * (wichtig für Canvas-Verarbeitung in Browsern).
   */
  static getFileViewUrlFromStorageUrl(storageUrl: string): string {
    try {
      if (!storageUrl) return '';
      const fileId = this.extractFileIdFromStorageUrl(storageUrl);
      if (!fileId) return storageUrl;
      const viewUrl = storage.getFileView(this.bucketId, fileId);
      return viewUrl.toString();
    } catch (error) {
      return storageUrl;
    }
  }

  /**
   * Erstellt eine Vorschau-URL für eine gespeicherte Datei-URL
   * Quick & dirty: extrahiert File-ID aus der URL
   */
  static getImagePreviewUrlFromStorageUrl(
    storageUrl: string,
    width: number,
    height: number
  ): string {
    try {
      if (!storageUrl) return '';
      const fileId = this.extractFileIdFromStorageUrl(storageUrl) || '';
      if (!fileId) return storageUrl;

      const previewUrl = storage.getFilePreview(
        this.bucketId,
        fileId,
        width,
        height
      );
      return previewUrl.toString();
    } catch (error) {
      return storageUrl;
    }
  }

  /**
   * Lädt einen Profilbild-Hintergrund hoch
   */
  static async uploadProfileBackground(file: File): Promise<string> {
    try {
      // Verwende die gleiche Logik wie uploadImage, aber mit eindeutiger ID
      const fileId = ID.unique();
      
      console.log('Uploading profile background:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        bucketId: this.bucketId,
        fileId
      });
      
      // Lade die Datei hoch
      const uploadedFile = await storage.createFile(
        this.bucketId,
        fileId,
        file
      );
      
      console.log('File uploaded successfully:', uploadedFile);
      
      // Für Canvas-Verarbeitung CORS-freundliche View-URL speichern
      const fileUrl = storage.getFileView(this.bucketId, uploadedFile.$id);
      
      return fileUrl.toString();
    } catch (error) {
      console.error('Upload error details:', error);
      
      // Detaillierte Fehlerbehandlung
      if (error instanceof Error) {
        if (error.message.includes('Bucket not found')) {
          throw new Error('Der Bildspeicher ist derzeit nicht verfügbar. Bitte kontaktiere die Administration.');
        } else if (error.message.includes('permission')) {
          throw new Error('Keine Berechtigung zum Hochladen. Bitte melde dich an.');
        } else if (error.message.includes('file size')) {
          throw new Error('Datei ist zu groß. Maximale Größe: 10MB');
        } else {
          throw new Error(`Upload-Fehler: ${error.message}`);
        }
      }
      
      throw new Error(`Fehler beim Hochladen des Profilbild-Hintergrunds: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
    }
  }

  /**
   * Prüft, ob eine Datei ein gültiger Profilbild-Hintergrund ist
   */
  static isValidProfileBackgroundFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB für Profilbild-Hintergründe
    
    return validTypes.includes(file.type) && file.size <= maxSize;
  }
}
